import React, { FC } from 'react';
import { Maybe } from 't2-graphql-types';
import { getNewArticlePageUrl } from 'src/services/pagesUrl/getNewArticlePageUrl';
import { Placeholder } from '../Placeholder/Placeholder';
import { PromptSettingsLiveStatus } from '../Prompt/constants';
import { BlockForDeactivatedUser } from '../BlockForDeactivatedUser/BlockForDeactivatedUser';
import LinkButton from '../LinkButton/LinkButton';
import Typo from '../Typography';
import Tooltip from '../Tooltip/Tooltip';
import { ReplyToPromptStatus } from './constants';
import Dropdown from '../Dropdown/Dropdown';
import { PlusIcon } from '../Icons/PlusIcon';
import DropdownItem from '../Dropdown/DropdownItem';
import DropdownClickableItem from '../Dropdown/DropdownClickableItem';
import Button from '../Button';
import { EditIcon } from '../Icons/EditIcon';
import { LinkIcon } from '../Icons/LinkIcon';
import { useModal } from '../Modal/useModal';
import { PromptLinkPostModalContentContainer } from '../PromptLinkPost/PromptLinkPostModalContentContainer';
import { usePromptPageContext } from '../Prompt/context/PromptPageContext';
import ProtectWithLogin from '../ProtectWithLogin/ProtectWithLogin';

type TPromptHeaderReplyButtonProps = {
  replyToPromptUrl: string;
  replyToPromptStatus: ReplyToPromptStatus;
  promptSettingsLiveStatus?: Maybe<PromptSettingsLiveStatus>;
  isPromptLoading?: boolean;
  isAuthenticatedUserLoading?: boolean;
  onReplyToPromptClick: () => void;
};

export const PromptHeaderReplyButton: FC<TPromptHeaderReplyButtonProps> = ({
  replyToPromptUrl,
  replyToPromptStatus,
  promptSettingsLiveStatus,
  isPromptLoading,
  isAuthenticatedUserLoading,
  onReplyToPromptClick,
}) => {
  if (isPromptLoading || isAuthenticatedUserLoading) {
    return <Placeholder className="w-32 h-9 md:h-10 rounded-4xl" />;
  }

  const isNewReplyToPrompt = replyToPromptStatus === ReplyToPromptStatus.NEW;
  const isContinuedReplyToPrompt = replyToPromptStatus === ReplyToPromptStatus.CONTINUE;
  const canReplyToPrompt = isNewReplyToPrompt || isContinuedReplyToPrompt;
  const isTimeConstrainedPrompt = !!promptSettingsLiveStatus;
  const isTimeConstrainedPromptUpcoming = promptSettingsLiveStatus === PromptSettingsLiveStatus.UPCOMING;
  const isTimeConstrainedPromptCompleted = promptSettingsLiveStatus === PromptSettingsLiveStatus.COMPLETED;
  const isTimeConstrainedPromptActive =
    isTimeConstrainedPrompt && promptSettingsLiveStatus === PromptSettingsLiveStatus.LIVE;

  const isDisabled = !canReplyToPrompt || (isTimeConstrainedPrompt && !isTimeConstrainedPromptActive);

  const getTooltipText = () => {
    if (!isTimeConstrainedPrompt || isTimeConstrainedPromptActive) {
      return '';
    }

    if (isTimeConstrainedPromptUpcoming) {
      return 'Replies are enabled once the prompt is live.';
    }

    if (isTimeConstrainedPromptCompleted) {
      return 'Replies are no longer accepted for this prompt.';
    }

    return '';
  };

  const getHref = () => {
    if (!isTimeConstrainedPrompt || isTimeConstrainedPromptActive) {
      return replyToPromptUrl;
    }

    return '#';
  };

  const getTitle = () => {
    if (isContinuedReplyToPrompt) {
      return 'Continue writing';
    }

    return 'Reply to prompt';
  };

  return (
    <div className="min-h-10">
      <BlockForDeactivatedUser>
        <>
          {isDisabled && getTooltipText() && (
            <Tooltip
              direction="bottom"
              cursor="default"
              trigger={<LinkButton className="px-7" href={getHref()} disabled title={getTitle()} />}
            >
              <Typo variant="small-p" className="!text-sm">
                {getTooltipText()}
              </Typo>
            </Tooltip>
          )}
          {isDisabled && !getTooltipText() && (
            <LinkButton
              className="px-7"
              href={getHref()}
              disabled={isDisabled}
              title={getTitle()}
              target="_blank"
              rel="noreferrer"
              onClick={onReplyToPromptClick}
            />
          )}
          {!isDisabled && <PromptReplyDropdownButton />}
        </>
      </BlockForDeactivatedUser>
    </div>
  );
};

const PromptReplyDropdownButton = () => {
  const { territoryId, promptId } = usePromptPageContext();
  const { ModalComponent, openModal, isOpen } = useModal();
  return (
    <>
      <ProtectWithLogin>
        {/* This div is required for ProtectWithLogin to work */}
        <div>
          <Dropdown
            renderLabel={() => (
              <Button tag="div" rounded>
                <div className="flex gap-2 items-center">
                  <PlusIcon className="stroke-white-900" />
                  Reply to prompt
                </div>
              </Button>
            )}
            menu={[
              <DropdownItem key="aa">
                <DropdownClickableItem
                  type="link"
                  leftAdornment={<EditIcon className="size-full" />}
                  className="items-center"
                  href={getNewArticlePageUrl({ parentArticleId: promptId })}
                >
                  <div className="w-full text-left">New Territory Post</div>
                </DropdownClickableItem>
              </DropdownItem>,
              <DropdownItem key="a">
                <DropdownClickableItem
                  leftAdornment={<LinkIcon className="size-full" />}
                  className="items-center"
                  onClick={openModal}
                >
                  <div className="w-full text-left">Link an existing Post</div>
                </DropdownClickableItem>
              </DropdownItem>,
            ]}
          />
        </div>
      </ProtectWithLogin>
      <PromptLinkPostModalContentContainer
        modalComponent={ModalComponent}
        territoryId={territoryId}
        promptId={promptId}
        isOpen={isOpen}
      />
    </>
  );
};
